import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { getHealthStatus, getSystemVersion } from 'graphql/queries';
import { validateSession } from 'store/tokenHandling/validateSession';

import { errorHandler } from './errors';

import type { HealthStatusInformation, SystemVersion } from 'API';
import { HealthStatusInformationSchema, SystemVersionSchema } from 'APIzod';

const getHealthStatusFn = async () => {
    const { data } = await client.graphql({ query: getHealthStatus });
    const res = HealthStatusInformationSchema().safeParse(data.getHealthStatus);

    if (!res.success) {
        errorHandler('Unexpected error occurred while getting system health status', res.error);
    }

    await validateSession();
    return { data: data.getHealthStatus };
};

const getSystemVersionFn = async () => {
    try {
        const { data } = await client.graphql({ query: getSystemVersion });
        return { data: SystemVersionSchema().parse(data.getSystemVersion) };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while getting the system api version', error);
    }
};

export const systemApi = createApi({
    reducerPath: 'system',
    baseQuery: graphqlRequestBaseQuery({
        url: '/graphql',
    }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        getHealthStatus: builder.query<HealthStatusInformation, string>({
            queryFn: getHealthStatusFn,
        }),
        getSystemVersion: builder.query<SystemVersion, string>({
            queryFn: getSystemVersionFn,
        }),
    }),
});

export const { useGetHealthStatusQuery, useLazyGetSystemVersionQuery } = systemApi;
